import { supabase } from '../lib/supabase.js';
import { OrderCommunicationManager } from '../lib/orderCommunication.js';

export class CommunicationTester {
  constructor() {
    this.communicationManager = new OrderCommunicationManager();
    this.statusTriggers = [
      { value: '', label: 'All Triggers' },
      { value: 'pending_pickup', label: 'Pending Pickup' },
      { value: 'driver_dispatched_pickup', label: 'Driver Dispatched (Pickup)' },
      { value: 'picked_up', label: 'Picked Up' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'ironing_complete', label: 'Ironing Complete' },
      { value: 'ready_for_delivery', label: 'Ready for Delivery' },
      { value: 'driver_dispatched_delivery', label: 'Driver Dispatched (Delivery)' },
      { value: 'out_for_delivery', label: 'Out for Delivery' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' },
      { value: 'payment_succeeded', label: 'Payment Succeeded' },
      { value: 'payment_failed', label: 'Payment Failed' },
      { value: 'payment_canceled', label: 'Payment Canceled' },
      { value: 'payment_initiated', label: 'Payment Initiated' }
    ];
  }

  render() {
    return `
      <div class="bg-white rounded-lg shadow p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-6">Communication System Tester</h2>
        
        <div class="space-y-6">
          <!-- Test Order Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Select Test Order
            </label>
            <select id="test-order-select" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500">
              <option value="">Loading orders...</option>
            </select>
          </div>

          <!-- Status Trigger Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Test Communication Trigger
            </label>
            <select id="communication-trigger-select" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500">
              <option value="">Select a trigger to test...</option>
            </select>
          </div>

          <!-- Order Details Display -->
          <div id="order-details" class="hidden bg-gray-50 p-4 rounded-md">
            <h3 class="font-medium text-gray-900 mb-2">Order Details</h3>
            <div id="order-info"></div>
          </div>

          <!-- Communication Tests -->
          <div class="grid grid-cols-1 gap-4">
            <button 
              id="test-selected-trigger" 
              class="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
              disabled
            >
              Test Selected Trigger
            </button>
          </div>

          <!-- System Diagnostics -->
          <div class="border-t pt-6">
            <h3 class="font-medium text-gray-900 mb-4">System Diagnostics</h3>
            
            <!-- Status Trigger Filter -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Filter Templates by Status Trigger
              </label>
              <select id="status-trigger-filter" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500">
              </select>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button 
                id="check-templates" 
                class="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                Check Templates
              </button>
              
              <button 
                id="check-history" 
                class="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                Check History
              </button>
              
              <button 
                id="test-edge-function" 
                class="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                Test Edge Function
              </button>
            </div>
          </div>

          <!-- Results Display -->
          <div id="test-results" class="hidden">
            <h3 class="font-medium text-gray-900 mb-2">Test Results</h3>
            <div id="results-content" class="bg-gray-50 p-4 rounded-md max-h-96 overflow-y-auto">
            </div>
          </div>
        </div>
      </div>
    `;
  }

  async bindEvents() {
    await this.loadOrders();
    this.populateCommunicationTriggerDropdown();
    this.populateStatusTriggerDropdown();
    
    const orderSelect = document.getElementById('test-order-select');
    const orderDetails = document.getElementById('order-details');
    const orderInfo = document.getElementById('order-info');
    const statusTriggerFilter = document.getElementById('status-trigger-filter');
    const communicationTriggerSelect = document.getElementById('communication-trigger-select');
    
    // Order selection handler
    orderSelect.addEventListener('change', (e) => {
      const orderId = e.target.value;
      if (orderId) {
        this.displayOrderDetails(orderId);
        this.enableTestButtons();
        orderDetails.classList.remove('hidden');
      } else {
        orderDetails.classList.add('hidden');
        this.disableTestButtons();
      }
    });

    // Communication trigger selection handler
    communicationTriggerSelect.addEventListener('change', () => {
      this.updateTestButton();
    });

    // Status trigger filter handler
    statusTriggerFilter.addEventListener('change', () => {
      this.checkTemplates();
    });

    // Test button handlers
    document.getElementById('test-selected-trigger').addEventListener('click', () => {
      const selectedTrigger = communicationTriggerSelect.value;
      if (selectedTrigger) {
        this.testCommunication(selectedTrigger);
      }
    });

    // Diagnostic button handlers
    document.getElementById('check-templates').addEventListener('click', () => {
      this.checkTemplates();
    });

    document.getElementById('check-history').addEventListener('click', () => {
      this.checkCommunicationHistory();
    });

    document.getElementById('test-edge-function').addEventListener('click', () => {
      this.testEdgeFunction();
    });
  }

  populateCommunicationTriggerDropdown() {
    const select = document.getElementById('communication-trigger-select');
    select.innerHTML = '<option value="">Select a trigger to test...</option>';
    
    // Filter out 'All Triggers' option for communication testing
    const communicationTriggers = this.statusTriggers.filter(trigger => trigger.value !== '');
    
    communicationTriggers.forEach(trigger => {
      const option = document.createElement('option');
      option.value = trigger.value;
      option.textContent = trigger.label;
      select.appendChild(option);
    });
  }

  populateStatusTriggerDropdown() {
    const select = document.getElementById('status-trigger-filter');
    select.innerHTML = '';
    
    this.statusTriggers.forEach(trigger => {
      const option = document.createElement('option');
      option.value = trigger.value;
      option.textContent = trigger.label;
      select.appendChild(option);
    });
  }

  updateTestButton() {
    const communicationTriggerSelect = document.getElementById('communication-trigger-select');
    const testButton = document.getElementById('test-selected-trigger');
    const selectedTrigger = communicationTriggerSelect.value;
    
    if (selectedTrigger) {
      const triggerLabel = this.statusTriggers.find(t => t.value === selectedTrigger)?.label || selectedTrigger;
      testButton.textContent = `Test ${triggerLabel}`;
      testButton.disabled = !document.getElementById('test-order-select').value;
    } else {
      testButton.textContent = 'Test Selected Trigger';
      testButton.disabled = true;
    }
  }

  async loadOrders() {
    try {
      const { data: orders, error } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          status,
          payment_status,
          total_amount,
          customers (
            full_name,
            email,
            phone
          )
        `)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      const select = document.getElementById('test-order-select');
      select.innerHTML = '<option value="">Select an order...</option>';
      
      orders.forEach(order => {
        const option = document.createElement('option');
        option.value = order.id;
        option.textContent = `${order.order_number} - ${order.customers.full_name} - $${order.total_amount}`;
        select.appendChild(option);
      });

      this.orders = orders;
    } catch (error) {
      this.showResults('Error loading orders: ' + error.message, 'error');
    }
  }

  displayOrderDetails(orderId) {
    const order = this.orders.find(o => o.id === orderId);
    if (!order) return;

    const orderInfo = document.getElementById('order-info');
    orderInfo.innerHTML = `
      <div class="grid grid-cols-2 gap-4 text-sm">
        <div><strong>Order:</strong> ${order.order_number}</div>
        <div><strong>Status:</strong> ${order.status}</div>
        <div><strong>Payment:</strong> ${order.payment_status}</div>
        <div><strong>Amount:</strong> $${order.total_amount}</div>
        <div><strong>Customer:</strong> ${order.customers.full_name}</div>
        <div><strong>Email:</strong> ${order.customers.email}</div>
        <div><strong>Phone:</strong> ${order.customers.phone}</div>
      </div>
    `;
  }

  enableTestButtons() {
    this.updateTestButton();
  }

  disableTestButtons() {
    const testButton = document.getElementById('test-selected-trigger');
    testButton.disabled = true;
    testButton.textContent = 'Test Selected Trigger';
  }

  async testCommunication(trigger) {
    const orderId = document.getElementById('test-order-select').value;
    if (!orderId) return;

    this.showResults('Testing communication for trigger: ' + trigger, 'info');

    try {
      const result = await this.communicationManager.triggerCommunication(orderId, trigger);
      this.showResults('Communication test result: ' + JSON.stringify(result, null, 2), 'success');
    } catch (error) {
      this.showResults('Communication test failed: ' + error.message, 'error');
    }
  }

  async checkTemplates() {
    const selectedTrigger = document.getElementById('status-trigger-filter').value;
    const filterText = selectedTrigger ? ` for trigger: ${selectedTrigger}` : '';
    
    this.showResults(`Checking communication templates${filterText}...`, 'info');

    try {
      let query = supabase
        .from('communication_templates')
        .select('*')
        .order('status_trigger', { ascending: true });

      // Apply filter if a specific trigger is selected
      if (selectedTrigger) {
        query = query.eq('status_trigger', selectedTrigger);
      }

      const { data: templates, error } = await query;

      if (error) throw error;

      const templatesByTrigger = templates.reduce((acc, template) => {
        if (!acc[template.status_trigger]) {
          acc[template.status_trigger] = [];
        }
        acc[template.status_trigger].push(template);
        return acc;
      }, {});

      let result = `Found ${templates.length} template(s)${filterText}:\n\n`;
      
      if (templates.length === 0) {
        result += selectedTrigger 
          ? `No templates found for trigger: ${selectedTrigger}`
          : 'No templates found in the system';
      } else {
        Object.entries(templatesByTrigger).forEach(([trigger, triggerTemplates]) => {
          result += `${trigger}:\n`;
          triggerTemplates.forEach(template => {
            result += `  - ${template.type}: ${template.name}\n`;
            if (template.subject) {
              result += `    Subject: ${template.subject}\n`;
            }
            result += `    Body: ${template.body.substring(0, 100)}...\n`;
          });
          result += '\n';
        });
      }

      this.showResults(result, 'success');
    } catch (error) {
      this.showResults('Error checking templates: ' + error.message, 'error');
    }
  }

  async checkCommunicationHistory() {
    const orderId = document.getElementById('test-order-select').value;
    if (!orderId) {
      this.showResults('Please select an order first', 'error');
      return;
    }

    this.showResults('Checking communication history...', 'info');

    try {
      const { data: history, error } = await supabase
        .from('communication_history')
        .select('*')
        .eq('order_id', orderId)
        .order('sent_at', { ascending: false });

      if (error) throw error;

      if (history.length === 0) {
        this.showResults('No communication history found for this order', 'warning');
        return;
      }

      let result = `Found ${history.length} communication record(s):\n\n`;
      history.forEach(record => {
        result += `${record.type.toUpperCase()} - ${record.status} - ${new Date(record.sent_at).toLocaleString()}\n`;
        if (record.subject) result += `Subject: ${record.subject}\n`;
        result += `Body: ${record.body.substring(0, 100)}...\n`;
        if (record.external_id) result += `External ID: ${record.external_id}\n`;
        result += '\n';
      });

      this.showResults(result, 'success');
    } catch (error) {
      this.showResults('Error checking history: ' + error.message, 'error');
    }
  }

  async testEdgeFunction() {
    this.showResults('Testing edge function connectivity...', 'info');

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/handle-order-status-change`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          record: { id: 'test', status: 'test' },
          old_record: { id: 'test', status: 'old_test' }
        })
      });

      const result = await response.text();
      
      if (response.ok) {
        this.showResults('Edge function is accessible and responding', 'success');
      } else {
        this.showResults(`Edge function error (${response.status}): ${result}`, 'error');
      }
    } catch (error) {
      this.showResults('Edge function connectivity test failed: ' + error.message, 'error');
    }
  }

  showResults(message, type = 'info') {
    const resultsDiv = document.getElementById('test-results');
    const contentDiv = document.getElementById('results-content');
    
    resultsDiv.classList.remove('hidden');
    
    const timestamp = new Date().toLocaleTimeString();
    const colorClass = {
      'info': 'text-violet-600',
      'success': 'text-green-600', 
      'warning': 'text-yellow-600',
      'error': 'text-red-600'
    }[type] || 'text-gray-600';
    
    const resultHtml = `
      <div class="mb-4 p-3 border-l-4 border-${type === 'error' ? 'red' : type === 'success' ? 'green' : type === 'warning' ? 'yellow' : 'violet'}-400 bg-${type === 'error' ? 'red' : type === 'success' ? 'green' : type === 'warning' ? 'yellow' : 'violet'}-50">
        <div class="flex justify-between items-start">
          <pre class="${colorClass} text-sm whitespace-pre-wrap">${message}</pre>
          <span class="text-xs text-gray-500">${timestamp}</span>
        </div>
      </div>
    `;
    
    contentDiv.insertAdjacentHTML('afterbegin', resultHtml);
  }
}